import React from 'react'
import { ComponentCardForm } from '../../../common/types/cardForm'
import Text from '../../../common/components/GeneralTextInput/generalTextInput'
import EmailInputRecipient from '../../../common/components/EmailInputRecipient'
import styled from 'styled-components'
import { card } from '../../../common/components/CardEditor/index'
import { getMailSettings, setMailSettings } from './helper'
import { MailSettings } from '../../../common/types/api/report'

import { components, IndicatorProps, GroupTypeBase } from 'react-select'
import { OptionType } from '../../../common/types/utilities'

import getSMTPConfigurations from '../../../api/getSMTPConfigurations'
import ListAsync from '../LookupsList/ListAsync'
import LiveTooltip from 'live/components/ui/tooltip'
import i18n from 'live/services/i18n'

export function ReportInformation({
    editMode,
    formObject: mailSettings,
    validations,
    onChange
}: ComponentCardForm<MailSettings>): JSX.Element {
    return (
        <StyledReportInformation
            onClick={() => {
                editMode = true
                onChange({ ...mailSettings })
            }}
        >
            <Text
                title={i18n('E-mail Subject')}
                placeholder={i18n('Ex.: e-mail subject')}
                required={true}
                tooltip={i18n('The subject of the report e-mail')}
                value={mailSettings.subject}
                editMode={true}
                variant="secondary"
                error={validations.includes('E-mail Subject')}
                onChange={text => {
                    const newMailSettings = { ...mailSettings, subject: text }
                    onChange({ ...newMailSettings })
                }}
            />
            <div className={`reportInformation--dualBlock`}>
                <div className={`reportInformation--smtpInstance`} tabIndex={0}>
                    <div className={`reportInformation--smtpInstance__titleContainer`}>
                        <h4 className={`reportInformation--smtpInstance__titleContainer--title`}>
                            {i18n('SMTP Instance')}
                        </h4>
                        <LiveTooltip
                            className={'inputBoolean_tooltip'}
                            content={i18n(
                                'The SMTP instance is responsible for sending the emails to the respective recipients.'
                            )}
                            position={'top'}
                        >
                            <i className={'fas fa-info-circle'} />
                        </LiveTooltip>
                        <span className={`reportInformation--smtpInstance__titleContainer--asterisk`}>*</span>
                    </div>
                    <ListAsync<string[]>
                        placeholder={'SMTP Instance'}
                        getApi={getSMTPConfigurations}
                        value={
                            mailSettings.channels[0]
                                ? { label: mailSettings.channels[0], value: mailSettings.channels[0] }
                                : null
                        }
                        onChange={option => {
                            const selectedOption = option as OptionType
                            const value = selectedOption?.value as string

                            // TODO: fix pointers (do this using report context)
                            mailSettings.channels = Array.of(value)
                            const newMailSettings = { ...mailSettings, channels: Array.of(value) }
                            onChange({ ...newMailSettings })
                        }}
                        callback={resultCallback => {
                            return resultCallback.map(option => ({ value: option, label: option }))
                        }}
                        disabled={!editMode}
                        editMode={editMode}
                        useLabels={false}
                        isMulti={false}
                        variant={'secondary'}
                    />
                </div>
                <EmailInputRecipient
                    title="Recipient in Case of error"
                    tooltip={i18n(
                        'If there is an error in sending the report, these will be the people who will receive the details of the occurrence of the error.'
                    )}
                    required={true}
                    variant={'secondary'}
                    values={mailSettings.errorRecipients}
                    editMode={editMode}
                    displayMsg={false}
                    showTotalEmail={false}
                    onChange={recipientList => {
                        mailSettings.errorRecipients = [...recipientList]
                        const newMailSettings = { ...mailSettings, errorRecipients: Array.of(...recipientList) }
                        onChange({ ...newMailSettings })
                    }}
                />
            </div>
        </StyledReportInformation>
    )
}

const StyledReportInformation = styled.div`
    > div {
        padding: 7px 20px;
        border: none;
    }
    > div:nth-child(1),
    > div:nth-child(2) {
        border-bottom: 1px solid ${({ theme }) => theme['gray-200']};
    }
    > div:nth-child(1) {
        border-right: none;
    }
    > div:nth-child(3) {
        border-right: 1px solid ${({ theme }) => theme['gray-200']};
    }

    .reportInformation {
        &--dualBlock {
            display: grid;
            grid-template-columns: 1fr 1fr;
            padding: 0px;
            margin: 0px;
        }
        &--smtpInstance {
            padding: 7px 15px;
            transition: 0.3s;
            background-color: ${({ theme }) => theme['gray-0']};
            &:focus-within,
            &:focus,
            &:target {
                background-color: ${({ theme }) => theme['blue-200']};
            }
            &__titleContainer {
                display: flex;
                align-items: center;
                margin-top: 10px;
                margin-left: 8px;
                i {
                    color: ${({ theme }) => theme['blue-500']};
                    margin-left: 6px;
                }
                &--title {
                    font-family: ${({ theme }) => theme['font-semibold']};
                    color: ${({ theme }) => theme['gray-800']};
                }
                &--asterisk {
                    margin-left: 5px;
                    font-size: 13px;
                    color: ${({ theme }) => theme['red-700']};
                }
            }
            &__dropdown-indicator {
                color: ${({ theme }) => theme['blue-500']};
            }
        }
    }
`
export const DropdownIndicator = (
    props: IndicatorProps<OptionType, boolean, GroupTypeBase<OptionType>>
): JSX.Element => {
    return (
        components.DropdownIndicator && (
            <components.DropdownIndicator {...props}>
                {props.selectProps.menuIsOpen ? (
                    <i className={'fas fa-caret-up'} />
                ) : (
                    <i className={'fas fa-caret-down'} />
                )}
            </components.DropdownIndicator>
        )
    )
}

export default card<MailSettings>({
    title: i18n('REPORT INFORMATION'),
    get: getMailSettings,
    set: setMailSettings
})(ReportInformation)
